import { ColDef, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    aggridHeaderClass, aggridSwitchButtonWrapperStyle, SmallTitleSubRow, SmallTitleWithSubSpan, SummaryChip,
} from 'src/components/common/common';
import { GridCalendar } from 'src/components/common/form/GridCalendar';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import {
    createCalendarCellClickHandler,
    CreateCellClickHandler,
    CustomCellClickAction,
    createCellCssClassRules,
    createGridDropDownCommitAfter,
    getSwitchButtonColumnOptions,
    GridCellConfig,
    InsertRowHandler,
    useRowSelectionHandler,
} from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';
import { showWarning } from 'src/utils/toast/toastUtil';
import { defaultHRM1030_R01TrainingDto, HRM1030_R01TrainingDto } from './Dto_HRM1030_R01';
import { useHRM1030_R01CellChange } from './hooks/HRM1030_R01CellChange';
import { useHRM1030_R01Store, useHRM1030_R01Store_GridCalendar } from './Store_HRM1030_R01';
import {
    formatHRM1030_R01Number,
    getHRM1030_R01Summary,
    getHRM1030_R01Today,
    HRM1030_R01_LAW_MIN_COUNT,
    HRM1030_R01_SAFETY_MIN_HOURS,
} from './Util_HRM1030_R01';

/** 교육 이력에서 편집 가능 표시(손가락 커서)를 할 컬럼이다. */
const EDITABLE_COLUMNS = ['trainNm', 'trainType', 'institution', 'startDt', 'endDt', 'trainHours',
    'costAmt', 'refundAmt', 'completeYn', 'certNo', 'remark'];
/** 셀 클릭·키보드 이동으로 셀 편집(입력칸·공통 드롭다운)을 시작할 컬럼이다. 날짜는 달력, 이수는 스위치로 바꾼다. */
const CELL_EDIT_COLUMNS = ['trainNm', 'trainType', 'institution', 'trainHours', 'costAmt', 'refundAmt', 'certNo', 'remark'];
/** 컬럼 설정 저장과 복원에 쓰는 교육 그리드 키이다. */
const COLUMN_STATE_KEY = 'HRM1030_R01-training-grid-column-state';
/** 공통 컬럼 복원 함수에 전달하는 교육 그리드 제목이다. */
const HEADER_NAMES = {
    rowIndex: '', trainSeq: '순번', trainNm: '교육과정명', trainType: '구분', institution: '교육기관',
    startDt: '시작일', endDt: '종료일', trainHours: '시간', costAmt: '교육비', refundAmt: '환급액',
    completeYn: '이수', certNo: '수료번호', fileCount: '수료증', remark: '비고',
};

/** 교육 그리드 참조와 교육 행 선택 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1030_R01TrainingDto> | null>; // 교육 그리드
    onTrainingSelect: (row: HRM1030_R01TrainingDto) => void; // 교육 행 클릭 시 첨부 대상 변경과 첨부 조회
}

/**
 * 선택 사원의 교육훈련 이력을 편집하는 그리드와 요약 줄이다.
 * @param gridRef 교육 그리드 참조
 * @param onTrainingSelect 교육 행 클릭 시 첨부 대상 교육을 바꾸고 첨부를 불러오는 함수
 * @returns 요약 줄과 교육 편집 그리드 JSX
 */
export const TrainingTable_HRM1030_R01 = ({ gridRef, onTrainingSelect }: Props) => {
    const { selectedHRM1030_R01, HRM1030_R01TrainingList, originalHRM1030_R01TrainingList,
        updateHRM1030_R01TrainingList, removeHRM1030_R01TrainingList,
        selectedHRM1030_R01TrainingList, setSelectedHRM1030_R01TrainingList,
        addSelectedHRM1030_R01TrainingList, removeSelectedHRM1030_R01TrainingList,
        deletedHRM1030_R01TrainingList, HRM1030_R01FileList, loadedHRM1030_R01FileTrainingList,
        deletedHRM1030_R01FileList,
        trainingColumnState, setTrainingColumnState, trainingScrollTop, setTrainingScrollTop } = useHRM1030_R01Store();
    // 앱 초기화 때 공통 코드 Store가 읽어 둔 교육구분(TRAIN_TYPE) 목록. 구분 컬럼 공통 드롭다운에 쓴다.
    const { trainTypeList } = useCommonCodeStore();
    // true이면 행 클릭을 선택으로 처리한다. +/- 버튼 직후에는 false가 되어 같은 클릭을 한 번 무시한다(작업지시와 같음).
    const [clickable, setClickable] = useState(true);

    const { handleTrainingCellChange } = useHRM1030_R01CellChange();
    const { HRM1030_R01GridCalendarState, setHRM1030_R01GridCalendarState } = useHRM1030_R01Store_GridCalendar();
    const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = HRM1030_R01GridCalendarState;
    const {
        getRowId,
        renderIndexCell,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
        handleSwitchButtonHandler,
    } = useGridSettingContent<HRM1030_R01TrainingDto>({
        gridRef,
        key: 'valueIndex',
        gridData: HRM1030_R01TrainingList,
        defaultGridData: defaultHRM1030_R01TrainingDto,
        selectedRow: selectedHRM1030_R01TrainingList,
        originalGridData: originalHRM1030_R01TrainingList,
        updateRow: updateHRM1030_R01TrainingList,
    });
    // 선택 사원 요약 값. 의존성: 교육 목록과 D 표시 목록
    const summary = useMemo(
        () => getHRM1030_R01Summary(HRM1030_R01TrainingList, deletedHRM1030_R01TrainingList),
        [HRM1030_R01TrainingList, deletedHRM1030_R01TrainingList],
    );
    // 그리드에 넘길 행 목록. 첨부를 불러온 교육은 화면 첨부 목록(D 제외)으로 수료증 건수를 다시 센다.
    const displayGridData = useMemo(() => paddedGridData.map(row => {
        if (!loadedHRM1030_R01FileTrainingList.includes(String(row.valueIndex)) && !row.inserted) return row;
        const fileCount = HRM1030_R01FileList.filter(file => file.trainingValueIndex === row.valueIndex
            && !deletedHRM1030_R01FileList.includes(String(file.valueIndex))).length;
        // 펼치기: 원래 행을 복사하고 수료증 건수만 덮어쓴다.
        return { ...row, fileCount };
    }), [paddedGridData, loadedHRM1030_R01FileTrainingList, HRM1030_R01FileList, deletedHRM1030_R01FileList]);
    /**
     * 선택 사원의 D 표시되지 않은 교육 행만 편집할 수 있다.
     * @param row 확인할 교육 행
     * @returns 편집할 수 있으면 true
     */
    const editable = useCallback(
        (row?: HRM1030_R01TrainingDto) => !!row && !!selectedHRM1030_R01
            && !deletedHRM1030_R01TrainingList.includes(String(row.valueIndex)),
        [selectedHRM1030_R01, deletedHRM1030_R01TrainingList],
    );
    /**
     * 신규 교육 행의 -는 저장 전 행과 그 교육의 신규 첨부를 즉시 제거한다. 저장된 행은 액션바 삭제로 D 표시한다.
     * @param row -를 누른 교육 행
     */
    const handleRemoveRow = useCallback((row: HRM1030_R01TrainingDto) => {
        if (!row.inserted) return;
        // - 버튼을 누른 클릭이 행 선택으로 번지지 않게 한다(공통 RemoveRowHandler와 같음).
        setClickable(false);
        removeHRM1030_R01TrainingList(row.valueIndex);
        setSelectedHRM1030_R01TrainingList(
            selectedHRM1030_R01TrainingList.filter(id => id !== String(row.valueIndex)),
        );
    }, [removeHRM1030_R01TrainingList, setSelectedHRM1030_R01TrainingList, selectedHRM1030_R01TrainingList]);
    /**
     * 교육 행을 추가한다. index의 +는 클릭한 행 뒤에, 빈 그리드의 +는 맨 끝(첫 행)에 추가한다.
     * @param event 클릭 이벤트
     * @param rowIndex +를 누른 행의 표시 순서. 없으면 맨 끝에 추가한다.
     */
    const handleTrainingInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number) => {
        const state = useHRM1030_R01Store.getState();
        // ?.: 선택 사원이 없으면 undefined가 된다.
        const empNo = state.selectedHRM1030_R01?.empNo;
        if (!empNo) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }
        const today = getHRM1030_R01Today();
        // 교육 기본 행을 복사하고 선택 사원번호, 오늘 날짜, 신규 표시를 덮어쓴다.
        const newTraining = { ...defaultHRM1030_R01TrainingDto, empNo, startDt: today, endDt: today, inserted: true };
        if (rowIndex === undefined) {
            state.insertHRM1030_R01TrainingList(newTraining);
            return;
        }
        /** index의 +는 공통 함수를 통해 클릭한 교육 행 뒤에 삽입한다. */
        InsertRowHandler({
            event,
            dataList: state.HRM1030_R01TrainingList,
            rowData: state.HRM1030_R01TrainingList[rowIndex],
            defaultRowData: newTraining,
            insertRow: (rows, index) => rows.forEach(row =>
                state.insertHRM1030_R01TrainingList(row, index)),
            // + 버튼을 누른 클릭이 행 선택으로 번지지 않게 공통 함수가 clickable을 false로 바꾼다.
            setClickable,
        });
    }, []);
    /**
     * 공통 index 렌더러로 행 상태와 행별 +, - 버튼을 표시한다.
     * @param params 행 번호 셀 정보
     * @returns 번호 또는 I·U·D 표시와 +, - 버튼 JSX
     */
    const renderTrainingIndexCell = useCallback((params: ICellRendererParams<HRM1030_R01TrainingDto>) =>
        renderIndexCell(
            params,
            // 행 상태 판단: 공통 함수가 선택·삭제·신규·수정 여부를 계산해 반환한다.
            innerParams => {
                const flags = getRowStyleFlags(innerParams, {
                    deletedList: deletedHRM1030_R01TrainingList,
                    selectedList: selectedHRM1030_R01TrainingList,
                    dataList: HRM1030_R01TrainingList,
                });
                /** 공통 상태 함수가 받는 문자열 ID와 실제 숫자 ID를 맞춘다. */
                const rowId = String(innerParams.data?.valueIndex);
                // 공통 계산 결과를 복사하고 선택·삭제 여부만 문자열 ID 기준으로 덮어쓴다.
                return {
                    ...flags,
                    isDeleted: deletedHRM1030_R01TrainingList.includes(rowId),
                    isSelected: selectedHRM1030_R01TrainingList.includes(rowId),
                };
            },
            (rowIndex: number, event: React.MouseEvent) => handleTrainingInsertClick(event, rowIndex),
            handleRemoveRow,
        ), [renderIndexCell, deletedHRM1030_R01TrainingList, selectedHRM1030_R01TrainingList,
        HRM1030_R01TrainingList, handleTrainingInsertClick, handleRemoveRow]);
    // 공통 드롭다운에서 값을 고른 뒤 다음 편집 컬럼으로 포커스를 넘기는 함수. 컬럼 목록이 상수라 한 번만 만든다.
    const handleGridDropDownCommitAfter = useMemo(
        () => createGridDropDownCommitAfter<HRM1030_R01TrainingDto>(CELL_EDIT_COLUMNS),
        [],
    );
    /**
     * 공통 달력에서 고른 날짜를 교육 행에 반영한다. 정렬된 상태에서도 맞는 행을 고치도록 표시 순서로 행을 찾는다.
     * 날짜를 지우면(삭제) 시작·종료일은 NOT NULL이라 오늘로 둔다.
     * @param date 고른 날짜. 삭제하면 undefined
     * @param rowIndex 달력을 연 행의 표시 순서
     * @param field 달력을 연 컬럼 (startDt 또는 endDt)
     */
    const handleGridCalendarChange = useCallback((date: Date | undefined, rowIndex: number | null, field: string | null) => {
        if (rowIndex == null || !field) return;
        // ?.: 표시 순서의 행 데이터에서 화면 행 번호를 꺼낸다. 없으면 undefined
        const valueIndex = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data?.valueIndex;
        const foundData = useHRM1030_R01Store.getState().HRM1030_R01TrainingList.find(row => row.valueIndex === valueIndex);
        if (!foundData) return;
        const newValue = formatDate(date ?? new Date());
        if (foundData[field as 'startDt' | 'endDt'] === newValue) return;
        // 셀 직접 편집과 같이, 저장된 행은 날짜가 바뀌면 수정 상태로 표시한다.
        updateHRM1030_R01TrainingList(foundData.valueIndex, {
            ...foundData,
            [field]: newValue,
            isModified: foundData.inserted ? foundData.isModified : true,
        });
    }, [gridRef, updateHRM1030_R01TrainingList]);
    /**
     * 달력을 다시 열 때 해당 행의 현재 날짜를 달력에 표시한다. 값이 없으면 오늘이다.
     * @param rowIndex 달력을 연 행의 표시 순서
     * @param field 달력을 연 컬럼
     */
    const handleGridCalendarLoad = useCallback((rowIndex: number, field: string) => {
        const rowData = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data;
        const dateValue = rowData ? rowData[field as 'startDt' | 'endDt'] : '';
        // 삼항 연산자: 날짜가 있으면 그 날짜, 없으면 오늘을 달력에 표시한다.
        setHRM1030_R01GridCalendarState({ targetDate: dateValue ? new Date(dateValue) : new Date() });
    }, [gridRef, setHRM1030_R01GridCalendarState]);
    /** 행별 index 열과 교육 정보 열을 정의한다. */
    const baseColumnDefs = useMemo<ColDef<HRM1030_R01TrainingDto>[]>(() => [
        // 공통 행 번호 컬럼 설정 배열을 풀어 넣는다.
        ...getDefaultRowIndexColumnConfig(renderTrainingIndexCell),
        {
            headerName: '순번', colId: 'trainSeq', field: 'trainSeq', width: 52,
            // 신규 행의 순번(null)은 빈칸으로 표시한다. 순번은 서버가 매긴다.
            valueFormatter: params => params.value == null ? '' : String(params.value),
        },
        {
            headerName: '교육과정명', colId: 'trainNm', field: 'trainNm', width: 210,
            // DB 컬럼 길이 100자까지 입력받는다.
            editable: params => editable(params.data), cellEditorParams: { maxLength: 100 },
        },
        {
            headerName: '구분', colId: 'trainType', field: 'trainType', width: 92,
            // 공통 드롭다운(GridDropDownForm). PRD2010_R01 작업지시의 지시구분과 같은 CommonCodeColumn 구성이다.
            // 펼치기: 공통 함수가 만든 편집기·코드명 표시 설정을 컬럼에 넣는다.
            ...CommonCodeColumn.options(
                trainTypeList,
                params => editable(params.data),
                CommonCodeColumn.change(updateHRM1030_R01TrainingList),
                handleGridDropDownCommitAfter,
            ),
        },
        {
            headerName: '교육기관', colId: 'institution', field: 'institution', width: 140,
            editable: params => editable(params.data), cellEditorParams: { maxLength: 100 },
        },
        {
            // 날짜는 셀 클릭 시 공통 달력(GridCalendar)으로 고친다. 공통 Grid_Excel은 편집 가능한 셀에서만
            // 셀 클릭 함수를 부르므로 editable은 켜 두고, 클릭 동작에서 달력을 연다(PRD2010_R01 작업일자와 같음).
            headerName: '시작일', colId: 'startDt', field: 'startDt', width: 118,
            editable: params => editable(params.data),
        },
        {
            headerName: '종료일', colId: 'endDt', field: 'endDt', width: 118,
            editable: params => editable(params.data),
        },
        {
            headerName: '시간', colId: 'trainHours', field: 'trainHours', width: 62,
            editable: params => editable(params.data), cellEditor: 'agNumberCellEditor',
            valueFormatter: params => formatHRM1030_R01Number(params.value, 1),
            // 숫자가 아니면 0으로 둔다.
            valueParser: params => Number(params.newValue) || 0,
        },
        {
            headerName: '교육비', colId: 'costAmt', field: 'costAmt', width: 96,
            editable: params => editable(params.data), cellEditor: 'agNumberCellEditor',
            valueFormatter: params => formatHRM1030_R01Number(params.value),
            valueParser: params => Number(params.newValue) || 0,
        },
        {
            headerName: '환급액', colId: 'refundAmt', field: 'refundAmt', width: 90,
            editable: params => editable(params.data), cellEditor: 'agNumberCellEditor',
            valueFormatter: params => formatHRM1030_R01Number(params.value),
            valueParser: params => Number(params.newValue) || 0,
        },
        {
            headerName: '이수', colId: 'completeYn', field: 'completeYn', width: 56,
            // 공통 스위치 버튼(AggridSwitchButton). SYS2010_R01 메뉴등록의 사용여부와 같은 구성이다.
            // 스위치를 바꾸면 공통 handleSwitchButtonHandler가 원본과 비교해 수정 상태를 정한다.
            ...getSwitchButtonColumnOptions<HRM1030_R01TrainingDto>(
                'completeYn',
                handleSwitchButtonHandler,
                HEADER_NAMES,
                aggridSwitchButtonWrapperStyle,
                params => editable(params.data),
            ),
        },
        {
            headerName: '수료번호', colId: 'certNo', field: 'certNo', width: 104,
            editable: params => editable(params.data), cellEditorParams: { maxLength: 50 },
        },
        {
            headerName: '수료증', colId: 'fileCount', field: 'fileCount', width: 66,
            // 첨부가 있으면 "n건", 없으면 빈칸
            valueFormatter: params => Number(params.value) > 0 ? `${params.value}건` : '',
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 140,
            // 비고는 DB 컬럼 길이 1000자까지 입력받는다.
            editable: params => editable(params.data), cellEditorParams: { maxLength: 1000 },
        },
    ], [trainTypeList, editable, renderTrainingIndexCell, updateHRM1030_R01TrainingList,
        handleGridDropDownCommitAfter, handleSwitchButtonHandler]);


    // 기본 컬럼 순서. 의존성: 컬럼 정의가 바뀔 때만 다시 만든다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);


    /** 저장된 교육 그리드의 컬럼 배치를 공통 함수로 복원한다(PRD2010_R01 작업지시와 같은 형태). */
    const columnDefs = useMemo(() => {
        if (trainingColumnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<HRM1030_R01TrainingDto>({
            baseColumnDefs,
            columnState: trainingColumnState,
            headerNames: HEADER_NAMES,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, trainingColumnState]);

    /** 선택·삭제·신규 셀 표시와 편집 가능한 컬럼 표시를 공통 규칙으로 만든다. 처음 한 번만 만든다. */
    const cellCssConfig = useMemo<GridCellConfig>(() => ({
        // 모든 행에서 비활성(회색)으로 표시할 컬럼. 순번은 서버가 매기고 수료증 건수는 계산 값이다.
        disabledCells: ['trainSeq', 'fileCount'],
        insertedDisabledCells: [],
        commonDisabledCells: [],
        pointerCells: EDITABLE_COLUMNS,
        insertedPointerCells: [],
        commonPointerCells: [],
        // 왼쪽 정렬할 컬럼
        alignLeftCells: ['trainNm', 'institution', 'certNo', 'remark'],
    }), []);


    // 위 설정을 신규 행용·기존 행용 컬럼 목록으로 합친 결과. 의존성: 설정 객체
    const cellCssColumns = useMemo(() => createCellCssClassRules(cellCssConfig), [cellCssConfig]);
    // 모든 컬럼에 적용할 기본 설정에 셀 CSS 규칙(선택·삭제·비활성·포인터·정렬)을 더한 객체
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM1030_R01TrainingDto>({
            idKey: 'valueIndex',
            // 공통 셀 규칙은 숫자 valueIndex와 비교하므로 문자열 ID를 숫자로 바꿔 넘긴다.
            selectedList: selectedHRM1030_R01TrainingList.map(Number),
            deletedList: deletedHRM1030_R01TrainingList.map(Number),
            ...cellCssColumns,
        }),
    }), [createCellClassRules, selectedHRM1030_R01TrainingList, deletedHRM1030_R01TrainingList, cellCssColumns]);


    /** 공통 이벤트가 셀 편집 컬럼 사이의 키보드 이동을 처리한다. 달력·스위치 컬럼은 키보드 편집 대상에서 뺀다. */
    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM1030_R01TrainingList,
        editableColumns: CELL_EDIT_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });
    // 셀 클릭 동작 목록: 시작일·종료일은 공통 달력을 열고, 그 밖의 편집 컬럼은 셀 편집(입력칸·공통 드롭다운)을 시작한다.
    // 의존성: 편집 판단 함수, 달력 상태와 변경 함수
    const cellClickActions = useMemo<CustomCellClickAction<HRM1030_R01TrainingDto>[]>(() => [
        {
            validate: (field, row) => field === 'startDt' && editable(row),
            action: createCalendarCellClickHandler<HRM1030_R01TrainingDto>('startDt', HRM1030_R01GridCalendarState, setHRM1030_R01GridCalendarState),
        },
        {
            validate: (field, row) => field === 'endDt' && editable(row),
            action: createCalendarCellClickHandler<HRM1030_R01TrainingDto>('endDt', HRM1030_R01GridCalendarState, setHRM1030_R01GridCalendarState),
        },
        {
            validate: (field, row) => CELL_EDIT_COLUMNS.includes(field) && editable(row),
            // !: rowIndex와 field가 반드시 있다고 TypeScript에 알려 준다.
            action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
        },
    ], [editable, HRM1030_R01GridCalendarState, setHRM1030_R01GridCalendarState]);
    // 클릭한 셀에 맞는 동작을 찾아 실행하는 셀 클릭 핸들러
    const handleCellClick = CreateCellClickHandler(HRM1030_R01TrainingList, 'valueIndex', cellClickActions);
    // 공통 행 선택 훅은 valueIndex를 숫자 그대로 비교하므로, 문자열로 저장한 선택·D 표시 목록을 숫자로 바꿔 넘긴다.
    // 훅 인자 타입이 string[]이라 변환 단언을 쓴다(공통 훅은 수정하지 않음). 의존성: 각 목록
    const selectedTrainingIdsForHook = useMemo(
        () => selectedHRM1030_R01TrainingList.map(Number) as unknown as string[],
        [selectedHRM1030_R01TrainingList],
    );
    const deletedTrainingIdsForHook = useMemo(
        () => deletedHRM1030_R01TrainingList.map(Number) as unknown as string[],
        [deletedHRM1030_R01TrainingList],
    );
    // 공통 행 선택 핸들러(PRD2010_R01 작업지시와 같음). 클릭하면 그 행만 선택하고, 선택된 행을 다시 누르면 해제한다.
    // D 표시 행은 선택하지 않고, +/- 직후 클릭은 한 번 무시한다.
    const handleRowSelection = useRowSelectionHandler<HRM1030_R01TrainingDto>({
        dataList: HRM1030_R01TrainingList,
        idKey: 'valueIndex',
        selectedList: selectedTrainingIdsForHook,
        deletedList: deletedTrainingIdsForHook,
        addSelected: addSelectedHRM1030_R01TrainingList,
        removeSelected: removeSelectedHRM1030_R01TrainingList,
        clickable, setClickable,
    });
    /**
     * 공통 행 선택을 실행한 뒤, 클릭한 교육을 첨부 대상으로 정한다. +/- 직후 무시되는 클릭은 첨부 대상도 바꾸지 않는다.
     * @param event 클릭한 행 정보
     */
    const handleRowClick = useCallback((event: RowClickedEvent<HRM1030_R01TrainingDto>) => {
        // true이면 이번 클릭이 행 선택으로 처리된다(하단 고정 행·빈 행·+/- 직후 클릭이 아님).
        const isSelectableClick = !!event.data && !event.node.rowPinned && clickable;
        handleRowSelection(event);
        if (isSelectableClick && event.data) onTrainingSelect(event.data);
    }, [handleRowSelection, clickable, onTrainingSelect]);


    /** 하단 고정 행: 순번 칸에 건수, 시간·교육비·환급액 칸에 합계를 표시한다. D 표시 행은 합계에서 뺀다. */
    const pinnedBottomRow = useMemo(() => {
        const liveRows = HRM1030_R01TrainingList.filter(row => !deletedHRM1030_R01TrainingList.includes(String(row.valueIndex)));
        return [{
            trainSeq: `${HRM1030_R01TrainingList.length}건`,
            trainHours: liveRows.reduce((total, row) => total + (Number(row.trainHours) || 0), 0),
            costAmt: liveRows.reduce((total, row) => total + (Number(row.costAmt) || 0), 0),
            refundAmt: liveRows.reduce((total, row) => total + (Number(row.refundAmt) || 0), 0),
        }];
    }, [HRM1030_R01TrainingList, deletedHRM1030_R01TrainingList]);

    // 선택·삭제·신규·수정 행 배경색 규칙
    const rowClassRules = createRowClassRules<HRM1030_R01TrainingDto>({
        idKey: 'valueIndex',
        selectedList: selectedHRM1030_R01TrainingList.map(Number),
        deletedList: deletedHRM1030_R01TrainingList.map(Number),
    });

    // 제목에 표시할 "사원번호 이름". 선택 사원이 없으면 빈 문자열이다.
    const employeeName = selectedHRM1030_R01
        ? `${selectedHRM1030_R01.empNo} ${selectedHRM1030_R01.empNm}` : '';

    // 교육 목록이 비면(교육 0건 사원 선택, 신규 행을 모두 지운 경우) 바로 입력할 수 있도록 빈 신규 행을 하나 넣는다.
    // PRD2010_R01·HRM2130_R01 표준 패턴과 같다. 사원을 고르기 전에는 넣을 사원번호가 없어 넣지 않는다.
    // 교육과정명이 빈 신규 행은 저장·변경 확인에서 제외되므로 이 행 때문에 저장이 막히지 않는다.
    // 의존성: 선택 사원과 교육 목록이 바뀔 때마다 비었는지 다시 확인한다.
    useEffect(() => {
        if (selectedHRM1030_R01 && HRM1030_R01TrainingList.length === 0) {
            handleTrainingInsertClick({} as React.MouseEvent);
        }
    }, [selectedHRM1030_R01, HRM1030_R01TrainingList, handleTrainingInsertClick]);

    return (
        // 영역 안을 클릭하면 마지막 작업 그리드를 교육으로 기록한다. 액션바 삭제·Excel 대상이 된다.
        <section className="flex flex-col h-full min-h-0"
            onClickCapture={() => useHRM1030_R01Store.getState().setActiveGridType('training')}>
            {/* 우클릭 메뉴, 전체 선택·선택 해제, 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트. 전체 선택은 D 표시 행을 뺀다. */}
            <GridContextWrapper<HRM1030_R01TrainingDto>
                gridRef={gridRef}
                paddedGridData={displayGridData}
                initSelected={() => setSelectedHRM1030_R01TrainingList([])}
                allSelected={() => setSelectedHRM1030_R01TrainingList(HRM1030_R01TrainingList
                    .filter(row => !deletedHRM1030_R01TrainingList.includes(String(row.valueIndex)))
                    .map(row => String(row.valueIndex)))}
                defaultRowData={defaultHRM1030_R01TrainingDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setTrainingColumnState}
                localStorageKey={COLUMN_STATE_KEY}>
                {/* 공통 영역 제목(파란 막대 + 제목). 오른쪽은 선택 사원을 표시한다. */}
                <SmallTitleWithSubSpan title="교육훈련 이력" subTitle={employeeName} />
                {/* 요약 줄: 선택 사원의 이수시간·교육비·환급액·법정교육 이수(2건 미만 빨간색)·산업안전(6시간 미만 경고색).
                    공통 보조 줄(SmallTitleSubRow)이 제목 아래 위·좌·우 테두리를 그리고, 항목마다 공통 요약 칩(SummaryChip)으로 구분한다. */}
                <SmallTitleSubRow className="gap-1.5">
                    <SummaryChip label="이수시간" value={`${formatHRM1030_R01Number(summary.completeHours, 1)}h`} tone="blue" />
                    <SummaryChip label="교육비" value={formatHRM1030_R01Number(summary.costAmt)} />
                    <SummaryChip label="환급액" value={formatHRM1030_R01Number(summary.refundAmt)} tone="green" />
                    {/* 삼항 연산자: 법정 이수 2건 미만이면 빨간색, 아니면 기본색 */}
                    <SummaryChip label="법정교육 이수" value={`${summary.lawCount}건`}
                        tone={summary.lawCount < HRM1030_R01_LAW_MIN_COUNT ? 'red' : 'default'} />
                    {/* 삼항 연산자: 산업안전 이수시간 6시간 미만이면 경고색, 아니면 기본색 */}
                    <SummaryChip label="산업안전" value={`${formatHRM1030_R01Number(summary.safetyHours, 1)}h`}
                        tone={summary.safetyHours < HRM1030_R01_SAFETY_MIN_HOURS ? 'orange' : 'default'} />
                </SmallTitleSubRow>
                {/* 교육 편집 그리드: 행·셀 CSS 규칙, 행 선택, 셀 클릭 편집, 키보드 이동, 값 변경, 하단 건수·합계 행을 연결한다. */}
                <Grid_Excel<HRM1030_R01TrainingDto>
                    ref={gridRef}
                    rowData={displayGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={rowClassRules}
                    onRowClicked={handleRowClick}
                    onCellClicked={handleCellClick}
                    onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                    onCellValueChanged={handleTrainingCellChange}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setTrainingColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={trainingScrollTop}
                    setScrollTop={setTrainingScrollTop} />
                {/* 시작일·종료일 공통 달력. PRD2010_R01 작업지시와 같은 GridCalendar를 셀 아래에 띄운다. */}
                <GridCalendar
                    rowIndex={targetRowIndex ?? 0}
                    targetField={targetField ?? ''}
                    visible={pickerVisible}
                    setVisible={visible => setHRM1030_R01GridCalendarState({ pickerVisible: visible })}
                    value={targetDate}
                    pickerPosition={pickerPosition}
                    setPickerPosition={position => setHRM1030_R01GridCalendarState({ pickerPosition: position })}
                    onClear={() => setHRM1030_R01GridCalendarState({ targetDate: undefined })}
                    onChange={handleGridCalendarChange}
                    onClickLoad={handleGridCalendarLoad} />
            </GridContextWrapper>
        </section>
    );
};
